import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// ─── Translations ─────────────────────────────────────────────────────────────

const resources = {
  en: {
    translation: {
      // Navigation
      nav: {
        dashboard: 'Dashboard',
        myTickets: 'My Tickets',
        allTickets: 'All Tickets',
        newTicket: 'New Ticket',
        knowledgeBase: 'Knowledge Base',
        liveChat: 'Live Chat',
        reports: 'Reports',
        users: 'Users',
        settings: 'Settings',
        profile: 'My Profile',
        signOut: 'Sign Out',
      },
      // Auth
      auth: {
        signIn: 'Sign In',
        signUp: 'Create Account',
        signOut: 'Sign Out',
        email: 'Email address',
        password: 'Password',
        forgotPassword: 'Forgot password?',
        resetPassword: 'Reset Password',
        rememberMe: 'Remember me',
        noAccount: "Don't have an account?",
        haveAccount: 'Already have an account?',
        sendResetLink: 'Send Reset Link',
        backToLogin: 'Back to login',
        firstName: 'First Name',
        lastName: 'Last Name',
      },
      // Tickets
      tickets: {
        title: 'Tickets',
        new: 'New Ticket',
        subject: 'Subject',
        description: 'Description',
        status: 'Status',
        priority: 'Priority',
        category: 'Category',
        assignedTo: 'Assigned To',
        createdAt: 'Created',
        submit: 'Submit Ticket',
        reply: 'Reply',
        internalNote: 'Internal Note',
        sendReply: 'Send Reply',
        addNote: 'Add Note',
        noTickets: 'No tickets found.',
        open: 'Open',
        inProgress: 'In Progress',
        pending: 'Pending',
        resolved: 'Resolved',
        closed: 'Closed',
        tags: 'Tags',
        attachments: 'Attachments',
        attachScreenshots: 'Attach screenshots or files (optional, max 5)',
        otherSpecify: 'Please specify your issue type',
      },
      // Knowledge Base
      kb: {
        title: 'Knowledge Base',
        search: 'Search articles…',
        noArticles: 'No articles found.',
        readMore: 'Read more',
        allCategories: 'All Categories',
        featured: 'Featured',
        helpful: 'Was this helpful?',
        yes: 'Yes',
        no: 'No',
      },
      // Live Chat
      chat: {
        title: 'Live Chat',
        startChat: 'Start Chat',
        typeMessage: 'Type a message…',
        waiting: 'Waiting for a support specialist to join…',
        ended: 'This chat has ended.',
        endChat: 'End Chat',
        join: 'Join',
        noSessions: 'No chat sessions',
        newChat: 'Start New Chat',
        support: 'Support',
      },
      // Reports
      reports: {
        title: 'Reports & Analytics',
        totalTickets: 'Total Tickets',
        openTickets: 'Open Tickets',
        resolved: 'Resolved',
        avgCsat: 'Avg. CSAT',
        responses: 'responses',
        resolutionRate: 'resolution rate',
      },
      // Common
      common: {
        save: 'Save Changes',
        saving: 'Saving…',
        cancel: 'Cancel',
        close: 'Close',
        delete: 'Delete',
        edit: 'Edit',
        create: 'Create',
        search: 'Search',
        loading: 'Loading…',
        noData: 'No data available',
        back: 'Back',
        submit: 'Submit',
        confirm: 'Confirm',
        enable: 'Enable',
        disable: 'Disable',
        language: 'Language',
      },
    },
  },

  fr: {
    translation: {
      nav: {
        dashboard: 'Tableau de bord',
        myTickets: 'Mes tickets',
        allTickets: 'Tous les tickets',
        newTicket: 'Nouveau ticket',
        knowledgeBase: 'Base de connaissances',
        liveChat: 'Chat en direct',
        reports: 'Rapports',
        users: 'Utilisateurs',
        settings: 'Paramètres',
        profile: 'Mon profil',
        signOut: 'Se déconnecter',
      },
      auth: {
        signIn: 'Se connecter',
        signUp: 'Créer un compte',
        signOut: 'Se déconnecter',
        email: 'Adresse e-mail',
        password: 'Mot de passe',
        forgotPassword: 'Mot de passe oublié ?',
        resetPassword: 'Réinitialiser le mot de passe',
        rememberMe: 'Se souvenir de moi',
        noAccount: "Vous n'avez pas de compte ?",
        haveAccount: 'Vous avez déjà un compte ?',
        sendResetLink: 'Envoyer le lien',
        backToLogin: 'Retour à la connexion',
        firstName: 'Prénom',
        lastName: 'Nom',
      },
      tickets: {
        title: 'Tickets',
        new: 'Nouveau ticket',
        subject: 'Sujet',
        description: 'Description',
        status: 'Statut',
        priority: 'Priorité',
        category: 'Catégorie',
        assignedTo: 'Assigné à',
        createdAt: 'Créé le',
        submit: 'Soumettre le ticket',
        reply: 'Répondre',
        internalNote: 'Note interne',
        sendReply: 'Envoyer la réponse',
        addNote: 'Ajouter une note',
        noTickets: 'Aucun ticket trouvé.',
        open: 'Ouvert',
        inProgress: 'En cours',
        pending: 'En attente',
        resolved: 'Résolu',
        closed: 'Fermé',
        tags: 'Étiquettes',
        attachments: 'Pièces jointes',
        attachScreenshots: "Joindre des captures d'écran ou fichiers (optionnel, max 5)",
        otherSpecify: 'Veuillez préciser votre type de problème',
      },
      kb: {
        title: 'Base de connaissances',
        search: 'Rechercher des articles…',
        noArticles: 'Aucun article trouvé.',
        readMore: 'Lire la suite',
        allCategories: 'Toutes les catégories',
        featured: 'À la une',
        helpful: 'Ceci vous a-t-il aidé ?',
        yes: 'Oui',
        no: 'Non',
      },
      chat: {
        title: 'Chat en direct',
        startChat: 'Démarrer le chat',
        typeMessage: 'Tapez un message…',
        waiting: "En attente d'un agent…",
        ended: 'Cette session de chat est terminée.',
        endChat: 'Terminer le chat',
        join: 'Rejoindre',
        noSessions: 'Aucune session de chat',
        newChat: 'Nouveau chat',
        support: 'Support',
      },
      reports: {
        title: 'Rapports et analyses',
        totalTickets: 'Total des tickets',
        openTickets: 'Tickets ouverts',
        resolved: 'Résolus',
        avgCsat: 'CSAT moyen',
        responses: 'réponses',
        resolutionRate: 'taux de résolution',
      },
      common: {
        save: 'Enregistrer',
        saving: 'Enregistrement…',
        cancel: 'Annuler',
        close: 'Fermer',
        delete: 'Supprimer',
        edit: 'Modifier',
        create: 'Créer',
        search: 'Rechercher',
        loading: 'Chargement…',
        noData: 'Aucune donnée disponible',
        back: 'Retour',
        submit: 'Soumettre',
        confirm: 'Confirmer',
        enable: 'Activer',
        disable: 'Désactiver',
        language: 'Langue',
      },
    },
  },

  es: {
    translation: {
      nav: {
        dashboard: 'Panel',
        myTickets: 'Mis tickets',
        allTickets: 'Todos los tickets',
        newTicket: 'Nuevo ticket',
        knowledgeBase: 'Base de conocimiento',
        liveChat: 'Chat en vivo',
        reports: 'Informes',
        users: 'Usuarios',
        settings: 'Configuración',
        profile: 'Mi perfil',
        signOut: 'Cerrar sesión',
      },
      auth: {
        signIn: 'Iniciar sesión',
        signUp: 'Crear cuenta',
        signOut: 'Cerrar sesión',
        email: 'Correo electrónico',
        password: 'Contraseña',
        forgotPassword: '¿Olvidó su contraseña?',
        resetPassword: 'Restablecer contraseña',
        rememberMe: 'Recordarme',
        noAccount: '¿No tiene una cuenta?',
        haveAccount: '¿Ya tiene una cuenta?',
        sendResetLink: 'Enviar enlace',
        backToLogin: 'Volver al inicio de sesión',
        firstName: 'Nombre',
        lastName: 'Apellido',
      },
      tickets: {
        title: 'Tickets',
        new: 'Nuevo ticket',
        subject: 'Asunto',
        description: 'Descripción',
        status: 'Estado',
        priority: 'Prioridad',
        category: 'Categoría',
        assignedTo: 'Asignado a',
        createdAt: 'Creado',
        submit: 'Enviar ticket',
        reply: 'Responder',
        internalNote: 'Nota interna',
        sendReply: 'Enviar respuesta',
        addNote: 'Agregar nota',
        noTickets: 'No se encontraron tickets.',
        open: 'Abierto',
        inProgress: 'En progreso',
        pending: 'Pendiente',
        resolved: 'Resuelto',
        closed: 'Cerrado',
        tags: 'Etiquetas',
        attachments: 'Archivos adjuntos',
        attachScreenshots: 'Adjuntar capturas o archivos (opcional, máx 5)',
        otherSpecify: 'Por favor especifique su tipo de problema',
      },
      kb: {
        title: 'Base de conocimiento',
        search: 'Buscar artículos…',
        noArticles: 'No se encontraron artículos.',
        readMore: 'Leer más',
        allCategories: 'Todas las categorías',
        featured: 'Destacado',
        helpful: '¿Fue esto útil?',
        yes: 'Sí',
        no: 'No',
      },
      chat: {
        title: 'Chat en vivo',
        startChat: 'Iniciar chat',
        typeMessage: 'Escribe un mensaje…',
        waiting: 'Esperando a que un agente se una…',
        ended: 'Esta sesión de chat ha finalizado.',
        endChat: 'Finalizar chat',
        join: 'Unirse',
        noSessions: 'No hay sesiones de chat',
        newChat: 'Nuevo chat',
        support: 'Soporte',
      },
      reports: {
        title: 'Informes y análisis',
        totalTickets: 'Total de tickets',
        openTickets: 'Tickets abiertos',
        resolved: 'Resueltos',
        avgCsat: 'CSAT promedio',
        responses: 'respuestas',
        resolutionRate: 'tasa de resolución',
      },
      common: {
        save: 'Guardar cambios',
        saving: 'Guardando…',
        cancel: 'Cancelar',
        close: 'Cerrar',
        delete: 'Eliminar',
        edit: 'Editar',
        create: 'Crear',
        search: 'Buscar',
        loading: 'Cargando…',
        noData: 'No hay datos disponibles',
        back: 'Atrás',
        submit: 'Enviar',
        confirm: 'Confirmar',
        enable: 'Habilitar',
        disable: 'Deshabilitar',
        language: 'Idioma',
      },
    },
  },

  de: {
    translation: {
      nav: {
        dashboard: 'Dashboard',
        myTickets: 'Meine Tickets',
        allTickets: 'Alle Tickets',
        newTicket: 'Neues Ticket',
        knowledgeBase: 'Wissensdatenbank',
        liveChat: 'Live-Chat',
        reports: 'Berichte',
        users: 'Benutzer',
        settings: 'Einstellungen',
        profile: 'Mein Profil',
        signOut: 'Abmelden',
      },
      auth: {
        signIn: 'Anmelden',
        signUp: 'Konto erstellen',
        signOut: 'Abmelden',
        email: 'E-Mail-Adresse',
        password: 'Passwort',
        forgotPassword: 'Passwort vergessen?',
        resetPassword: 'Passwort zurücksetzen',
        rememberMe: 'Angemeldet bleiben',
        noAccount: 'Noch kein Konto?',
        haveAccount: 'Bereits ein Konto?',
        sendResetLink: 'Link senden',
        backToLogin: 'Zurück zur Anmeldung',
        firstName: 'Vorname',
        lastName: 'Nachname',
      },
      tickets: { title: 'Tickets', new: 'Neues Ticket', subject: 'Betreff', description: 'Beschreibung', status: 'Status', priority: 'Priorität', category: 'Kategorie', assignedTo: 'Zugewiesen an', createdAt: 'Erstellt', submit: 'Ticket einreichen', reply: 'Antworten', internalNote: 'Interne Notiz', sendReply: 'Antwort senden', addNote: 'Notiz hinzufügen', noTickets: 'Keine Tickets gefunden.', open: 'Offen', inProgress: 'In Bearbeitung', pending: 'Ausstehend', resolved: 'Gelöst', closed: 'Geschlossen', tags: 'Tags', attachments: 'Anhänge', attachScreenshots: 'Screenshots oder Dateien anhängen (optional, max. 5)', otherSpecify: 'Bitte Ihr Anliegen beschreiben' },
      kb: { title: 'Wissensdatenbank', search: 'Artikel suchen…', noArticles: 'Keine Artikel gefunden.', readMore: 'Mehr lesen', allCategories: 'Alle Kategorien', featured: 'Empfohlen', helpful: 'War das hilfreich?', yes: 'Ja', no: 'Nein' },
      chat: { title: 'Live-Chat', startChat: 'Chat starten', typeMessage: 'Nachricht eingeben…', waiting: 'Warten auf einen Agenten…', ended: 'Dieser Chat wurde beendet.', endChat: 'Chat beenden', join: 'Beitreten', noSessions: 'Keine Chat-Sitzungen', newChat: 'Neuer Chat', support: 'Support' },
      reports: { title: 'Berichte & Analysen', totalTickets: 'Tickets gesamt', openTickets: 'Offene Tickets', resolved: 'Gelöst', avgCsat: 'Ø CSAT', responses: 'Antworten', resolutionRate: 'Lösungsrate' },
      common: { save: 'Speichern', saving: 'Speichern…', cancel: 'Abbrechen', close: 'Schließen', delete: 'Löschen', edit: 'Bearbeiten', create: 'Erstellen', search: 'Suchen', loading: 'Laden…', noData: 'Keine Daten verfügbar', back: 'Zurück', submit: 'Einreichen', confirm: 'Bestätigen', enable: 'Aktivieren', disable: 'Deaktivieren', language: 'Sprache' },
    },
  },

  // ─── Indian Languages ───────────────────────────────────────────────────────

  hi: {
    translation: {
      nav: { dashboard: 'डैशबोर्ड', myTickets: 'मेरे टिकट', allTickets: 'सभी टिकट', newTicket: 'नया टिकट', knowledgeBase: 'ज्ञान आधार', liveChat: 'लाइव चैट', reports: 'रिपोर्ट', users: 'उपयोगकर्ता', settings: 'सेटिंग्स', profile: 'मेरी प्रोफ़ाइल', signOut: 'साइन आउट' },
      auth: { signIn: 'साइन इन', signUp: 'खाता बनाएं', signOut: 'साइन आउट', email: 'ईमेल पता', password: 'पासवर्ड', forgotPassword: 'पासवर्ड भूल गए?', resetPassword: 'पासवर्ड रीसेट करें', rememberMe: 'मुझे याद रखें', noAccount: 'खाता नहीं है?', haveAccount: 'पहले से खाता है?', sendResetLink: 'लिंक भेजें', backToLogin: 'लॉगिन पर वापस', firstName: 'पहला नाम', lastName: 'अंतिम नाम' },
      tickets: { title: 'टिकट', new: 'नया टिकट', subject: 'विषय', description: 'विवरण', status: 'स्थिति', priority: 'प्राथमिकता', category: 'श्रेणी', assignedTo: 'को सौंपा गया', createdAt: 'बनाया गया', submit: 'टिकट सबमिट करें', reply: 'जवाब दें', internalNote: 'आंतरिक नोट', sendReply: 'जवाब भेजें', addNote: 'नोट जोड़ें', noTickets: 'कोई टिकट नहीं मिला।', open: 'खुला', inProgress: 'प्रगति में', pending: 'लंबित', resolved: 'हल', closed: 'बंद', tags: 'टैग', attachments: 'अटैचमेंट', attachScreenshots: 'स्क्रीनशॉट संलग्न करें (वैकल्पिक, अधिकतम 5)', otherSpecify: 'कृपया अपनी समस्या बताएं' },
      kb: { title: 'ज्ञान आधार', search: 'लेख खोजें…', noArticles: 'कोई लेख नहीं मिला।', readMore: 'और पढ़ें', allCategories: 'सभी श्रेणियां', featured: 'विशेष', helpful: 'क्या यह सहायक था?', yes: 'हाँ', no: 'नहीं' },
      chat: { title: 'लाइव चैट', startChat: 'चैट शुरू करें', typeMessage: 'संदेश लिखें…', waiting: 'एजेंट की प्रतीक्षा…', ended: 'यह चैट समाप्त हो गई।', endChat: 'चैट समाप्त करें', join: 'जुड़ें', noSessions: 'कोई सत्र नहीं', newChat: 'नई चैट', support: 'सहायता' },
      reports: { title: 'रिपोर्ट और विश्लेषण', totalTickets: 'कुल टिकट', openTickets: 'खुले टिकट', resolved: 'हल', avgCsat: 'औसत CSAT', responses: 'प्रतिक्रियाएं', resolutionRate: 'समाधान दर' },
      common: { save: 'सहेजें', saving: 'सहेज रहे हैं…', cancel: 'रद्द करें', close: 'बंद करें', delete: 'हटाएं', edit: 'संपादित करें', create: 'बनाएं', search: 'खोजें', loading: 'लोड हो रहा है…', noData: 'कोई डेटा उपलब्ध नहीं', back: 'वापस', submit: 'सबमिट करें', confirm: 'पुष्टि करें', enable: 'सक्षम करें', disable: 'अक्षम करें', language: 'भाषा' },
    },
  },

  ta: {
    translation: {
      nav: { dashboard: 'டாஷ்போர்டு', myTickets: 'என் டிக்கெட்கள்', allTickets: 'அனைத்து டிக்கெட்கள்', newTicket: 'புதிய டிக்கெட்', knowledgeBase: 'அறிவுத் தளம்', liveChat: 'நேரடி அரட்டை', reports: 'அறிக்கைகள்', users: 'பயனர்கள்', settings: 'அமைப்புகள்', profile: 'என் சுயவிவரம்', signOut: 'வெளியேறு' },
      auth: { signIn: 'உள்நுழைக', signUp: 'கணக்கு உருவாக்கு', signOut: 'வெளியேறு', email: 'மின்னஞ்சல்', password: 'கடவுச்சொல்', forgotPassword: 'கடவுச்சொல் மறந்தீர்களா?', resetPassword: 'கடவுச்சொல் மீட்டமை', rememberMe: 'என்னை நினைவில் கொள்', noAccount: 'கணக்கு இல்லையா?', haveAccount: 'கணக்கு உள்ளதா?', sendResetLink: 'இணைப்பு அனுப்பு', backToLogin: 'உள்நுழைவுக்கு திரும்பு', firstName: 'முதல் பெயர்', lastName: 'கடைசி பெயர்' },
      tickets: { title: 'டிக்கெட்கள்', new: 'புதிய டிக்கெட்', subject: 'தலைப்பு', description: 'விவரம்', status: 'நிலை', priority: 'முன்னுரிமை', category: 'வகை', assignedTo: 'ஒதுக்கப்பட்டவர்', createdAt: 'உருவாக்கப்பட்டது', submit: 'டிக்கெட் சமர்ப்பி', reply: 'பதில்', internalNote: 'உள் குறிப்பு', sendReply: 'பதில் அனுப்பு', addNote: 'குறிப்பு சேர்', noTickets: 'டிக்கெட்கள் இல்லை.', open: 'திறந்தது', inProgress: 'நடப்பில்', pending: 'நிலுவை', resolved: 'தீர்வு', closed: 'மூடப்பட்டது', tags: 'குறிச்சொற்கள்', attachments: 'இணைப்புகள்', attachScreenshots: 'கோப்புகள் இணை (விரும்பினால், அதிகபட்சம் 5)', otherSpecify: 'உங்கள் சிக்கலை குறிப்பிடவும்' },
      kb: { title: 'அறிவுத் தளம்', search: 'கட்டுரை தேடு…', noArticles: 'கட்டுரைகள் இல்லை.', readMore: 'மேலும் படி', allCategories: 'அனைத்து வகைகள்', featured: 'சிறப்பு', helpful: 'பயனுள்ளதா?', yes: 'ஆம்', no: 'இல்லை' },
      chat: { title: 'நேரடி அரட்டை', startChat: 'அரட்டை தொடங்கு', typeMessage: 'செய்தி தட்டச்சு…', waiting: 'முகவர் காத்திருக்கிறார்…', ended: 'இந்த அரட்டை முடிந்தது.', endChat: 'அரட்டை முடி', join: 'சேரு', noSessions: 'அமர்வுகள் இல்லை', newChat: 'புதிய அரட்டை', support: 'ஆதரவு' },
      reports: { title: 'அறிக்கைகள் & பகுப்பாய்வு', totalTickets: 'மொத்த டிக்கெட்கள்', openTickets: 'திறந்த டிக்கெட்கள்', resolved: 'தீர்வு', avgCsat: 'சராசரி CSAT', responses: 'பதில்கள்', resolutionRate: 'தீர்வு விகிதம்' },
      common: { save: 'சேமி', saving: 'சேமிக்கிறது…', cancel: 'ரத்து', close: 'மூடு', delete: 'நீக்கு', edit: 'திருத்து', create: 'உருவாக்கு', search: 'தேடு', loading: 'ஏற்றுகிறது…', noData: 'தரவு இல்லை', back: 'திரும்பு', submit: 'சமர்ப்பி', confirm: 'உறுதிப்படுத்து', enable: 'இயக்கு', disable: 'முடக்கு', language: 'மொழி' },
    },
  },

  te: {
    translation: {
      nav: { dashboard: 'డాష్‌బోర్డ్', myTickets: 'నా టికెట్లు', allTickets: 'అన్ని టికెట్లు', newTicket: 'కొత్త టికెట్', knowledgeBase: 'జ్ఞాన భాండారం', liveChat: 'లైవ్ చాట్', reports: 'నివేదికలు', users: 'వినియోగదారులు', settings: 'సెట్టింగ్‌లు', profile: 'నా ప్రొఫైల్', signOut: 'సైన్ అవుట్' },
      auth: { signIn: 'సైన్ ఇన్', signUp: 'ఖాతా సృష్టించు', signOut: 'సైన్ అవుట్', email: 'ఇమెయిల్ చిరునామా', password: 'పాస్‌వర్డ్', forgotPassword: 'పాస్‌వర్డ్ మర్చిపోయారా?', resetPassword: 'పాస్‌వర్డ్ రీసెట్', rememberMe: 'నన్ను గుర్తుంచుకో', noAccount: 'ఖాతా లేదా?', haveAccount: 'ఇప్పటికే ఖాతా ఉందా?', sendResetLink: 'లింక్ పంపు', backToLogin: 'లాగిన్‌కు తిరిగి', firstName: 'మొదటి పేరు', lastName: 'చివరి పేరు' },
      tickets: { title: 'టికెట్లు', new: 'కొత్త టికెట్', subject: 'విషయం', description: 'వివరణ', status: 'స్థితి', priority: 'ప్రాధాన్యత', category: 'వర్గం', assignedTo: 'కేటాయించినవారు', createdAt: 'సృష్టించబడింది', submit: 'టికెట్ సమర్పించు', reply: 'జవాబు', internalNote: 'అంతర్గత గమనిక', sendReply: 'జవాబు పంపు', addNote: 'గమనిక జోడించు', noTickets: 'టికెట్లు కనుగొనబడలేదు.', open: 'తెరవబడింది', inProgress: 'పురోగతిలో', pending: 'పెండింగ్', resolved: 'పరిష్కరించబడింది', closed: 'మూసివేయబడింది', tags: 'ట్యాగ్లు', attachments: 'జోడింపులు', attachScreenshots: 'ఫైళ్లు జోడించు (ఐచ్ఛికం, గరిష్టం 5)', otherSpecify: 'దయచేసి మీ సమస్యను పేర్కొనండి' },
      kb: { title: 'జ్ఞాన భాండారం', search: 'వ్యాసం వెతకండి…', noArticles: 'వ్యాసాలు కనుగొనబడలేదు.', readMore: 'మరింత చదవండి', allCategories: 'అన్ని వర్గాలు', featured: 'విశేష', helpful: 'ఇది సహాయకరంగా ఉందా?', yes: 'అవును', no: 'కాదు' },
      chat: { title: 'లైవ్ చాట్', startChat: 'చాట్ ప్రారంభించు', typeMessage: 'సందేశం టైప్ చేయి…', waiting: 'ఏజెంట్ కోసం వేచి ఉన్నాము…', ended: 'ఈ చాట్ ముగిసింది.', endChat: 'చాట్ ముగించు', join: 'చేరు', noSessions: 'సెషన్‌లు లేవు', newChat: 'కొత్త చాట్', support: 'మద్దతు' },
      reports: { title: 'నివేదికలు & విశ్లేషణ', totalTickets: 'మొత్తం టికెట్లు', openTickets: 'తెరవబడిన టికెట్లు', resolved: 'పరిష్కరించబడింది', avgCsat: 'సగటు CSAT', responses: 'ప్రతిస్పందనలు', resolutionRate: 'పరిష్కార రేటు' },
      common: { save: 'సేవ్ చేయి', saving: 'సేవ్ అవుతోంది…', cancel: 'రద్దు చేయి', close: 'మూసివేయి', delete: 'తొలగించు', edit: 'సవరించు', create: 'సృష్టించు', search: 'వెతకు', loading: 'లోడ్ అవుతోంది…', noData: 'డేటా లేదు', back: 'వెనక్కి', submit: 'సమర్పించు', confirm: 'నిర్ధారించు', enable: 'ప్రారంభించు', disable: 'నిలిపివేయు', language: 'భాష' },
    },
  },

  bn: {
    translation: {
      nav: { dashboard: 'ড্যাশবোর্ড', myTickets: 'আমার টিকিট', allTickets: 'সব টিকিট', newTicket: 'নতুন টিকিট', knowledgeBase: 'জ্ঞানভাণ্ডার', liveChat: 'লাইভ চ্যাট', reports: 'রিপোর্ট', users: 'ব্যবহারকারী', settings: 'সেটিংস', profile: 'আমার প্রোফাইল', signOut: 'সাইন আউট' },
      auth: { signIn: 'সাইন ইন', signUp: 'অ্যাকাউন্ট তৈরি করুন', signOut: 'সাইন আউট', email: 'ইমেইল ঠিকানা', password: 'পাসওয়ার্ড', forgotPassword: 'পাসওয়ার্ড ভুলে গেছেন?', resetPassword: 'পাসওয়ার্ড রিসেট', rememberMe: 'মনে রাখুন', noAccount: 'অ্যাকাউন্ট নেই?', haveAccount: 'ইতিমধ্যে অ্যাকাউন্ট আছে?', sendResetLink: 'লিঙ্ক পাঠান', backToLogin: 'লগইনে ফিরুন', firstName: 'প্রথম নাম', lastName: 'শেষ নাম' },
      tickets: { title: 'টিকিট', new: 'নতুন টিকিট', subject: 'বিষয়', description: 'বিবরণ', status: 'স্থিতি', priority: 'অগ্রাধিকার', category: 'বিভাগ', assignedTo: 'নিযুক্ত', createdAt: 'তৈরি হয়েছে', submit: 'টিকিট জমা দিন', reply: 'উত্তর', internalNote: 'অভ্যন্তরীণ নোট', sendReply: 'উত্তর পাঠান', addNote: 'নোট যোগ করুন', noTickets: 'কোনো টিকিট পাওয়া যায়নি।', open: 'খোলা', inProgress: 'প্রক্রিয়াধীন', pending: 'মুলতুবি', resolved: 'সমাধান', closed: 'বন্ধ', tags: 'ট্যাগ', attachments: 'সংযুক্তি', attachScreenshots: 'স্ক্রিনশট যোগ করুন (ঐচ্ছিক, সর্বোচ্চ 5)', otherSpecify: 'অনুগ্রহ করে আপনার সমস্যা বর্ণনা করুন' },
      kb: { title: 'জ্ঞানভাণ্ডার', search: 'নিবন্ধ খুঁজুন…', noArticles: 'কোনো নিবন্ধ পাওয়া যায়নি।', readMore: 'আরও পড়ুন', allCategories: 'সব বিভাগ', featured: 'বিশেষ', helpful: 'এটা কি সহায়ক ছিল?', yes: 'হ্যাঁ', no: 'না' },
      chat: { title: 'লাইভ চ্যাট', startChat: 'চ্যাট শুরু করুন', typeMessage: 'বার্তা টাইপ করুন…', waiting: 'এজেন্টের অপেক্ষায়…', ended: 'এই চ্যাট শেষ হয়েছে।', endChat: 'চ্যাট শেষ করুন', join: 'যোগ দিন', noSessions: 'কোনো সেশন নেই', newChat: 'নতুন চ্যাট', support: 'সহায়তা' },
      reports: { title: 'রিপোর্ট ও বিশ্লেষণ', totalTickets: 'মোট টিকিট', openTickets: 'খোলা টিকিট', resolved: 'সমাধান', avgCsat: 'গড় CSAT', responses: 'সাড়া', resolutionRate: 'সমাধানের হার' },
      common: { save: 'সংরক্ষণ করুন', saving: 'সংরক্ষণ হচ্ছে…', cancel: 'বাতিল', close: 'বন্ধ করুন', delete: 'মুছুন', edit: 'সম্পাদনা করুন', create: 'তৈরি করুন', search: 'খুঁজুন', loading: 'লোড হচ্ছে…', noData: 'কোনো ডেটা নেই', back: 'পিছনে', submit: 'জমা দিন', confirm: 'নিশ্চিত করুন', enable: 'সক্রিয় করুন', disable: 'নিষ্ক্রিয় করুন', language: 'ভাষা' },
    },
  },

  mr: {
    translation: {
      nav: { dashboard: 'डॅशबोर्ड', myTickets: 'माझ्या तिकिटे', allTickets: 'सर्व तिकिटे', newTicket: 'नवीन तिकीट', knowledgeBase: 'ज्ञान आधार', liveChat: 'लाइव्ह चॅट', reports: 'अहवाल', users: 'वापरकर्ते', settings: 'सेटिंग्ज', profile: 'माझी प्रोफाइल', signOut: 'साइन आउट' },
      auth: { signIn: 'साइन इन', signUp: 'खाते तयार करा', signOut: 'साइन आउट', email: 'ईमेल पत्ता', password: 'पासवर्ड', forgotPassword: 'पासवर्ड विसरलात?', resetPassword: 'पासवर्ड रीसेट करा', rememberMe: 'मला लक्षात ठेवा', noAccount: 'खाते नाही?', haveAccount: 'आधीच खाते आहे?', sendResetLink: 'लिंक पाठवा', backToLogin: 'लॉगिनवर परत', firstName: 'पहिले नाव', lastName: 'आडनाव' },
      tickets: { title: 'तिकिटे', new: 'नवीन तिकीट', subject: 'विषय', description: 'वर्णन', status: 'स्थिती', priority: 'प्राधान्य', category: 'श्रेणी', assignedTo: 'नियुक्त', createdAt: 'तयार केले', submit: 'तिकीट सादर करा', reply: 'उत्तर द्या', internalNote: 'अंतर्गत नोंद', sendReply: 'उत्तर पाठवा', addNote: 'नोंद जोडा', noTickets: 'कोणतेही तिकीट आढळले नाही.', open: 'उघडे', inProgress: 'प्रगतीपथावर', pending: 'प्रलंबित', resolved: 'निराकरण', closed: 'बंद', tags: 'टॅग', attachments: 'संलग्नक', attachScreenshots: 'स्क्रीनशॉट जोडा (पर्यायी, जास्तीत जास्त 5)', otherSpecify: 'कृपया आपली समस्या नमूद करा' },
      kb: { title: 'ज्ञान आधार', search: 'लेख शोधा…', noArticles: 'कोणतेही लेख आढळले नाही.', readMore: 'अधिक वाचा', allCategories: 'सर्व श्रेणी', featured: 'वैशिष्ट्यीकृत', helpful: 'हे उपयुक्त होते का?', yes: 'होय', no: 'नाही' },
      chat: { title: 'लाइव्ह चॅट', startChat: 'चॅट सुरू करा', typeMessage: 'संदेश टाइप करा…', waiting: 'एजंटची प्रतीक्षा…', ended: 'हे चॅट संपले.', endChat: 'चॅट समाप्त करा', join: 'सामील व्हा', noSessions: 'कोणतेही सत्र नाही', newChat: 'नवीन चॅट', support: 'समर्थन' },
      reports: { title: 'अहवाल व विश्लेषण', totalTickets: 'एकूण तिकिटे', openTickets: 'उघडी तिकिटे', resolved: 'निराकरण', avgCsat: 'सरासरी CSAT', responses: 'प्रतिसाद', resolutionRate: 'निराकरण दर' },
      common: { save: 'जतन करा', saving: 'जतन होत आहे…', cancel: 'रद्द करा', close: 'बंद करा', delete: 'हटवा', edit: 'संपादित करा', create: 'तयार करा', search: 'शोधा', loading: 'लोड होत आहे…', noData: 'कोणताही डेटा नाही', back: 'मागे', submit: 'सादर करा', confirm: 'पुष्टी करा', enable: 'सक्षम करा', disable: 'अक्षम करा', language: 'भाषा' },
    },
  },

  gu: {
    translation: {
      nav: { dashboard: 'ડૅશબોર્ડ', myTickets: 'મારી ટિકિટ', allTickets: 'બધી ટિકિટ', newTicket: 'નવી ટિકિટ', knowledgeBase: 'જ્ઞાન આધાર', liveChat: 'લાઇવ ચૅટ', reports: 'અહેવાલ', users: 'વપરાશકર્તા', settings: 'સેટિંગ્સ', profile: 'મારી પ્રોફાઇલ', signOut: 'સાઇન આઉટ' },
      auth: { signIn: 'સાઇન ઇન', signUp: 'ખાતું બનાવો', signOut: 'સાઇન આઉટ', email: 'ઈ-મેઈલ સરનામું', password: 'પાસવર્ડ', forgotPassword: 'પાસવર્ડ ભૂલી ગયા?', resetPassword: 'પાસવર્ડ રીસેટ', rememberMe: 'મને યાદ રાખો', noAccount: 'ખાતું નથી?', haveAccount: 'ખાતું છે?', sendResetLink: 'લિંક મોકલો', backToLogin: 'લોગિન પર પાછા', firstName: 'પ્રથમ નામ', lastName: 'અટક' },
      tickets: { title: 'ટિકિટ', new: 'નવી ટિકિટ', subject: 'વિષય', description: 'વિગત', status: 'સ્થિતિ', priority: 'અગ્રતા', category: 'શ્રેણી', assignedTo: 'સોંપાયેલ', createdAt: 'બનાવ્યું', submit: 'ટિકિટ સબમિટ કરો', reply: 'જવાબ', internalNote: 'આંતરિક નોંધ', sendReply: 'જવાબ મોકલો', addNote: 'નોંધ ઉમેરો', noTickets: 'કોઈ ટિકિટ મળ્યા નહીં.', open: 'ઓપન', inProgress: 'ચાલી રહ્યું છે', pending: 'બાકી', resolved: 'ઉકેલાયેલ', closed: 'બંધ', tags: 'ટૅગ', attachments: 'જોડાણ', attachScreenshots: 'ફ઼ાઇલ જોડો (વૈકલ્પિક, મહત્તમ 5)', otherSpecify: 'કૃપા કરી સમસ્યાનું વર્ણન કરો' },
      kb: { title: 'જ્ઞાન આધાર', search: 'લેખ શોધો…', noArticles: 'કોઈ લેખ મળ્યો નહીં.', readMore: 'વધુ વાંચો', allCategories: 'બધી શ્રેણીઓ', featured: 'વિશેષ', helpful: 'શું આ મદ્દરૂપ હતું?', yes: 'હા', no: 'ના' },
      chat: { title: 'લાઇવ ચૅટ', startChat: 'ચૅટ શરૂ કરો', typeMessage: 'સંદેશ ટાઈપ કરો…', waiting: 'એજન્ટ માટે રાહ…', ended: 'આ ચૅટ સમાપ્ત થઈ.', endChat: 'ચૅટ સમાપ્ત', join: 'જોડાઓ', noSessions: 'કોઈ સત્ર નથી', newChat: 'નવી ચૅટ', support: 'સહાય' },
      reports: { title: 'અહેવાલ અને વિશ્લેષણ', totalTickets: 'કુલ ટિકિટ', openTickets: 'ઓપન ટિકિટ', resolved: 'ઉકેલાયેલ', avgCsat: 'સરેરાશ CSAT', responses: 'પ્રતિભાવ', resolutionRate: 'ઉકેલ દર' },
      common: { save: 'સાચવો', saving: 'સાચવી રહ્યા છીએ…', cancel: 'રદ કરો', close: 'બંધ કરો', delete: 'કાઢી નાખો', edit: 'સંપાદિત કરો', create: 'બનાવો', search: 'શોધો', loading: 'લૉડ થઈ રહ્યું છે…', noData: 'કોઈ ડેટા નથી', back: 'પાછળ', submit: 'સબમિટ', confirm: 'પુષ્ટિ', enable: 'સક્ષમ', disable: 'અક્ષમ', language: 'ભાષા' },
    },
  },

  kn: {
    translation: {
      nav: { dashboard: 'ಡ್ಯಾಶ್‌ಬೋರ್ಡ್', myTickets: 'ನನ್ನ ಟಿಕೆಟ್‌ಗಳು', allTickets: 'ಎಲ್ಲಾ ಟಿಕೆಟ್‌ಗಳು', newTicket: 'ಹೊಸ ಟಿಕೆಟ್', knowledgeBase: 'ಜ್ಞಾನ ಆಧಾರ', liveChat: 'ಲೈವ್ ಚಾಟ್', reports: 'ವರದಿಗಳು', users: 'ಬಳಕೆದಾರರು', settings: 'ಸೆಟ್ಟಿಂಗ್‌ಗಳು', profile: 'ನನ್ನ ಪ್ರೊಫೈಲ್', signOut: 'ಸೈನ್ ಔಟ್' },
      auth: { signIn: 'ಸೈನ್ ಇನ್', signUp: 'ಖಾತೆ ತೆರೆಯಿರಿ', signOut: 'ಸೈನ್ ಔಟ್', email: 'ಇಮೇಲ್ ವಿಳಾಸ', password: 'ಪಾಸ್‌ವರ್ಡ್', forgotPassword: 'ಪಾಸ್‌ವರ್ಡ್ ಮರೆತಿರಾ?', resetPassword: 'ಪಾಸ್‌ವರ್ಡ್ ರೀಸೆಟ್', rememberMe: 'ನನ್ನನ್ನು ನೆನಪಿಡಿ', noAccount: 'ಖಾತೆ ಇಲ್ಲವೇ?', haveAccount: 'ಖಾತೆ ಇದೆಯೇ?', sendResetLink: 'ಲಿಂಕ್ ಕಳುಹಿಸಿ', backToLogin: 'ಲಾಗಿನ್‌ಗೆ ಹಿಂತಿರುಗಿ', firstName: 'ಮೊದಲ ಹೆಸರು', lastName: 'ಕೊನೆಯ ಹೆಸರು' },
      tickets: { title: 'ಟಿಕೆಟ್‌ಗಳು', new: 'ಹೊಸ ಟಿಕೆಟ್', subject: 'ವಿಷಯ', description: 'ವಿವರಣೆ', status: 'ಸ್ಥಿತಿ', priority: 'ಆದ್ಯತೆ', category: 'ವರ್ಗ', assignedTo: 'ನಿಯೋಜಿತ', createdAt: 'ರಚಿಸಲಾಗಿದೆ', submit: 'ಟಿಕೆಟ್ ಸಲ್ಲಿಸಿ', reply: 'ಉತ್ತರ', internalNote: 'ಆಂತರಿಕ ಟಿಪ್ಪಣಿ', sendReply: 'ಉತ್ತರ ಕಳುಹಿಸಿ', addNote: 'ಟಿಪ್ಪಣಿ ಸೇರಿಸಿ', noTickets: 'ಟಿಕೆಟ್‌ಗಳು ಕಂಡುಬಂದಿಲ್ಲ.', open: 'ತೆರೆದಿದೆ', inProgress: 'ಪ್ರಗತಿಯಲ್ಲಿ', pending: 'ಬಾಕಿ', resolved: 'ಪರಿಹರಿಸಲಾಗಿದೆ', closed: 'ಮುಚ್ಚಲಾಗಿದೆ', tags: 'ಟ್ಯಾಗ್‌ಗಳು', attachments: 'ಲಗತ್ತುಗಳು', attachScreenshots: 'ಫೈಲ್‌ಗಳನ್ನು ಲಗತ್ತಿಸಿ (ಐಚ್ಛಿಕ, ಗರಿಷ್ಠ 5)', otherSpecify: 'ದಯವಿಟ್ಟು ಸಮಸ್ಯೆಯನ್ನು ವಿವರಿಸಿ' },
      kb: { title: 'ಜ್ಞಾನ ಆಧಾರ', search: 'ಲೇಖನ ಹುಡುಕಿ…', noArticles: 'ಲೇಖನಗಳು ಕಂಡುಬಂದಿಲ್ಲ.', readMore: 'ಇನ್ನಷ್ಟು ಓದಿ', allCategories: 'ಎಲ್ಲಾ ವರ್ಗಗಳು', featured: 'ವಿಶೇಷ', helpful: 'ಇದು ಸಹಾಯಕವಾಗಿತ್ತೇ?', yes: 'ಹೌದು', no: 'ಇಲ್ಲ' },
      chat: { title: 'ಲೈವ್ ಚಾಟ್', startChat: 'ಚಾಟ್ ಪ್ರಾರಂಭಿಸಿ', typeMessage: 'ಸಂದೇಶ ಟೈಪ್ ಮಾಡಿ…', waiting: 'ಏಜೆಂಟ್‌ಗಾಗಿ ಕಾಯುತ್ತಿದ್ದೇವೆ…', ended: 'ಈ ಚಾಟ್ ಮುಗಿದಿದೆ.', endChat: 'ಚಾಟ್ ಮುಗಿಸಿ', join: 'ಸೇರಿ', noSessions: 'ಸೆಷನ್‌ಗಳಿಲ್ಲ', newChat: 'ಹೊಸ ಚಾಟ್', support: 'ಬೆಂಬಲ' },
      reports: { title: 'ವರದಿಗಳು & ವಿಶ್ಲೇಷಣೆ', totalTickets: 'ಒಟ್ಟು ಟಿಕೆಟ್‌ಗಳು', openTickets: 'ತೆರೆದ ಟಿಕೆಟ್‌ಗಳು', resolved: 'ಪರಿಹರಿಸಲಾಗಿದೆ', avgCsat: 'ಸರಾಸರಿ CSAT', responses: 'ಪ್ರತಿಕ್ರಿಯೆಗಳು', resolutionRate: 'ಪರಿಹಾರ ದರ' },
      common: { save: 'ಉಳಿಸಿ', saving: 'ಉಳಿಸಲಾಗುತ್ತಿದೆ…', cancel: 'ರದ್ದು', close: 'ಮುಚ್ಚಿ', delete: 'ಅಳಿಸಿ', edit: 'ಸಂಪಾದಿಸಿ', create: 'ರಚಿಸಿ', search: 'ಹುಡುಕಿ', loading: 'ಲೋಡ್ ಆಗುತ್ತಿದೆ…', noData: 'ಡೇಟಾ ಇಲ್ಲ', back: 'ಹಿಂದೆ', submit: 'ಸಲ್ಲಿಸಿ', confirm: 'ದೃಢಪಡಿಸಿ', enable: 'ಸಕ್ರಿಯ', disable: 'ನಿಷ್ಕ್ರಿಯ', language: 'ಭಾಷೆ' },
    },
  },

  ml: {
    translation: {
      nav: { dashboard: 'ഡാഷ്‌ബോർഡ്', myTickets: 'എന്റെ ടിക്കറ്റുകൾ', allTickets: 'എല്ലാ ടിക്കറ്റുകളും', newTicket: 'പുതിയ ടിക്കറ്റ്', knowledgeBase: 'വിജ്ഞാന ശേഖരം', liveChat: 'ലൈവ് ചാറ്റ്', reports: 'റിപ്പോർട്ടുകൾ', users: 'ഉപയോക്താക്കൾ', settings: 'ക്രമീകരണങ്ങൾ', profile: 'എന്റെ പ്രൊഫൈൽ', signOut: 'സൈൻ ഔട്ട്' },
      auth: { signIn: 'സൈൻ ഇൻ', signUp: 'അക്കൗണ്ട് ഉണ്ടാക്കുക', signOut: 'സൈൻ ഔട്ട്', email: 'ഇ-മെയിൽ വിലാസം', password: 'പാസ്‌വേഡ്', forgotPassword: 'പാസ്‌വേഡ് മറന്നോ?', resetPassword: 'പാസ്‌വേഡ് പുനഃക്രമീകരിക്കുക', rememberMe: 'എന്നെ ഓർക്കുക', noAccount: 'അക്കൗണ്ട് ഇല്ലേ?', haveAccount: 'ഇതിനകം അക്കൗണ്ട് ഉണ്ടോ?', sendResetLink: 'ലിങ്ക് അയക്കുക', backToLogin: 'ലോഗിനിലേക്ക് മടങ്ങുക', firstName: 'ആദ്യ നാമം', lastName: 'കുടുംബ നാമം' },
      tickets: { title: 'ടിക്കറ്റുകൾ', new: 'പുതിയ ടിക്കറ്റ്', subject: 'വിഷയം', description: 'വിവരണം', status: 'നില', priority: 'മുൻഗണന', category: 'വിഭാഗം', assignedTo: 'നിയോഗിച്ചത്', createdAt: 'സൃഷ്ടിച്ചത്', submit: 'ടിക്കറ്റ് സമർപ്പിക്കുക', reply: 'മറുപടി', internalNote: 'ആന്തരിക കുറിപ്പ്', sendReply: 'മറുപടി അയക്കുക', addNote: 'കുറിപ്പ് ചേർക്കുക', noTickets: 'ടിക്കറ്റുകൾ കണ്ടെത്തിയില്ല.', open: 'തുറന്നത്', inProgress: 'പുരോഗതിയിൽ', pending: 'തീർപ്പാക്കാത്തത്', resolved: 'പരിഹരിച്ചത്', closed: 'അടച്ചത്', tags: 'ടാഗുകൾ', attachments: 'അറ്റാച്ച്‌മെന്റുകൾ', attachScreenshots: 'ഫയലുകൾ അറ്റാച്ച് ചെയ്യുക (ഐഛികം, പരമാവധി 5)', otherSpecify: 'ദയവായി നിങ്ങളുടെ പ്രശ്നം വിവരിക്കുക' },
      kb: { title: 'വിജ്ഞാന ശേഖരം', search: 'ലേഖനം തിരയുക…', noArticles: 'ലേഖനങ്ങൾ കണ്ടെത്തിയില്ല.', readMore: 'കൂടുതൽ വായിക്കുക', allCategories: 'എല്ലാ വിഭാഗങ്ങളും', featured: 'പ്രത്യേകം', helpful: 'ഇത് സഹായകരമായിരുന്നോ?', yes: 'അതെ', no: 'ഇല്ല' },
      chat: { title: 'ലൈവ് ചാറ്റ്', startChat: 'ചാറ്റ് തുടങ്ങുക', typeMessage: 'സന്ദേശം ടൈപ്പ് ചെയ്യുക…', waiting: 'ഏജന്റിനായി കാത്തിരിക്കുന്നു…', ended: 'ഈ ചാറ്റ് അവസാനിച്ചു.', endChat: 'ചാറ്റ് അവസാനിപ്പിക്കുക', join: 'ചേരുക', noSessions: 'സെഷനുകൾ ഒന്നുമില്ല', newChat: 'പുതിയ ചാറ്റ്', support: 'പിന്തുണ' },
      reports: { title: 'റിപ്പോർട്ടുകളും വിശകലനവും', totalTickets: 'ആകെ ടിക്കറ്റുകൾ', openTickets: 'തുറന്ന ടിക്കറ്റുകൾ', resolved: 'പരിഹരിച്ചത്', avgCsat: 'ശരാശരി CSAT', responses: 'പ്രതികരണങ്ങൾ', resolutionRate: 'പരിഹാര നിരക്ക്' },
      common: { save: 'സംരക്ഷിക്കുക', saving: 'സംരക്ഷിക്കുന്നു…', cancel: 'റദ്ദാക്കുക', close: 'അടയ്ക്കുക', delete: 'ഇല്ലാതാക്കുക', edit: 'തിരുത്തുക', create: 'സൃഷ്ടിക്കുക', search: 'തിരയുക', loading: 'ലോഡ് ചെയ്യുന്നു…', noData: 'ഡേറ്റ ഒന്നുമില്ല', back: 'തിരിച്ച്', submit: 'സമർപ്പിക്കുക', confirm: 'സ്ഥിരീകരിക്കുക', enable: 'പ്രവർത്തനക്ഷമം', disable: 'പ്രവർത്തനരഹിതം', language: 'ഭാഷ' },
    },
  },
};

// ─── Init ─────────────────────────────────────────────────────────────────────

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'uhelp_language',
    },
  });

export default i18n;

/** Available languages */
export const LANGUAGES = [
  // International
  { code: 'en', label: 'English',    flag: '🇬🇧' },
  { code: 'fr', label: 'Français',   flag: '🇫🇷' },
  { code: 'es', label: 'Español',    flag: '🇪🇸' },
  { code: 'de', label: 'Deutsch',    flag: '🇩🇪' },
  // Indian Languages
  { code: 'hi', label: 'हिंदी',       flag: '🇮🇳' },
  { code: 'ta', label: 'தமிழ்',       flag: '🇮🇳' },
  { code: 'te', label: 'తెలుగు',      flag: '🇮🇳' },
  { code: 'bn', label: 'বাংলা',       flag: '🇮🇳' },
  { code: 'mr', label: 'मराठी',       flag: '🇮🇳' },
  { code: 'gu', label: 'ગુજરાતી',     flag: '🇮🇳' },
  { code: 'kn', label: 'ಕನ್ನಡ',       flag: '🇮🇳' },
  { code: 'ml', label: 'മലയാളം',     flag: '🇮🇳' },
];
